import axios from 'axios'
import exceptionHandler from '@/common/exception-handler'

const getCompanyId = (companyId, isDefault) => isDefault ? 'default' : companyId

const isDefault = route => route.path.indexOf('default') !== -1

const getUserRoles = (companyId, isDefault = false) => {
  return axios.get(`/api/roles/${getCompanyId(companyId, isDefault)}`)
    .then(res => res.data)
    .catch(error => exceptionHandler.parse(error))
}

const getUserRole = (companyId, roleId, isDefault = false) => {
  return getUserRoles(companyId, isDefault)
    .then(res => {
      let role = res.roles.find(next => next.id === roleId)

      if (!role) {
        return Promise.reject(new Error(404))
      }

      return role
    })
}

const getPermissionGroups = (permissions) => {
  let permissionGroups = []

  permissions.forEach(permission => {
    let existing = permissionGroups.find(next => next.groupName === permission.group)
    if (!existing) {
      existing = { groupName: permission.group, permissions: [] }
      permissionGroups.push(existing)
    }
    existing.permissions.push(permission.name)
  })

  return permissionGroups
}

const saveUserRoles = (companyId, userRoles, isDefault = false) => {
  let changes = {}
  userRoles.filter(next => hasChanges(next)).forEach(role => { changes[role.id] = role.changes })

  return axios.post(`/api/roles/rolePermissions/${getCompanyId(companyId, isDefault)}`, changes)
    .catch(error => exceptionHandler.parse(error))
}

function hasChanges (role) {
  return role.changes.addPerms.length > 0 ||
    role.changes.removePerms.length > 0 ||
    role.changes.maxDiscountPct !== role.changes.currentMaxDiscountPct
}

const createUserRole = (companyId, userRole, isDefault = false) => {
  return axios.post(`/api/roles/${getCompanyId(companyId, isDefault)}`, userRole)
    .then(res => res.data)
    .catch(error => exceptionHandler.parse(error))
}

const deleteUserRole = (companyId, roleId, isDefault = false) => {
  return axios.post(`/api/roles/${getCompanyId(companyId, isDefault)}/${roleId}/delete`)
    .catch(error => exceptionHandler.parse(error))
}

const editUserRole = (companyId, userRole, isDefault = false) => {
  return axios.put(`/api/roles/${getCompanyId(companyId, isDefault)}`, userRole)
    .then(res => res.data)
    .catch(error => exceptionHandler.parse(error))
}

const cloneUserRole = (companyId, toCloneId, roleName, isDefault = false) => {
  return axios.post(`/api/roles/${getCompanyId(companyId, isDefault)}/${toCloneId}/clone?name=${roleName}`)
    .then(res => res.data)
    .catch(error => exceptionHandler.parse(error))
}

const changeRolePermission = (role, permission, value) => {
  let addPerms = role.changes.addPerms
  let removePerms = role.changes.removePerms

  if (value) {
    if (!role.grantedPermissions.includes(permission)) {
      role.grantedPermissions.push(permission)

      let index = removePerms.indexOf(permission)
      if (index >= 0) {
        removePerms.splice(index, 1)
      } else {
        addPerms.push(permission)
      }
    }
  } else {
    let index = role.grantedPermissions.indexOf(permission)
    if (index >= 0) {
      role.grantedPermissions.splice(index, 1)
    }

    index = addPerms.indexOf(permission)
    if (index >= 0) {
      addPerms.splice(index, 1)
    } else {
      removePerms.push(permission)
    }
  }
}

const getTenantTags = (companyId) => {
  return axios.get(`/api/tenants/tags/${companyId}`)
    .then(res => res.data)
    .catch(error => exceptionHandler.parse(error))
}

const calculateRevealPage = (revealId, roles, pageSize) => {
  let index = roles.findIndex(next => next.id === revealId)

  if (index === -1) {
    return 0
  }

  if (index + pageSize >= roles.length) {
    return index < pageSize ? 0 : roles.length - pageSize
  }

  return index
}

export default {
  isDefault,
  getUserRoles,
  getUserRole,
  saveUserRoles,
  cloneUserRole,
  getPermissionGroups,
  createUserRole,
  deleteUserRole,
  editUserRole,
  changeRolePermission,
  getTenantTags,
  calculateRevealPage
}
